import * as React from 'react';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { generalMessages } from 'src/registry/utils/messages';
import { cn } from 'src/utils/shadcn';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
    opts?: CarouselOptions;
    plugins?: CarouselPlugin;
    orientation?: 'horizontal' | 'vertical';
    setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
    carouselRef: ReturnType<typeof useEmblaCarousel>[0];
    api: ReturnType<typeof useEmblaCarousel>[1];
    scrollPrev: () => void;
    scrollNext: () => void;
    scrollTo: (index: number) => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
    const context = React.useContext(CarouselContext);

    if (!context) {
        throw new Error('useCarousel must be used within a <Carousel />');
    }

    return context;
}

function Carousel({
    orientation = 'horizontal',
    opts,
    setApi,
    plugins,
    className,
    children,
    ...props
}: React.ComponentProps<'div'> & CarouselProps) {
    const [carouselRef, api] = useEmblaCarousel(
        {
            ...opts,
            axis: orientation === 'horizontal' ? 'x' : 'y',
        },
        plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
        if (!api) return;
        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
        api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
        api?.scrollNext();
    }, [api]);

    const scrollTo = React.useCallback((index: number) => {
        api?.scrollTo(index);
    }, [api]);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                scrollPrev();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                scrollNext();
            }
        },
        [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
        if (!api || !setApi) return;
        setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
        if (!api) return;
        onSelect(api);
        api.on('reInit', onSelect);
        api.on('select', onSelect);

        return () => {
            api?.off('select', onSelect);
        };
    }, [api, onSelect]);

    return (
        <CarouselContext.Provider
            value={{
                carouselRef,
                api: api,
                opts,
                orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
                scrollPrev,
                scrollNext,
                scrollTo,
                canScrollPrev,
                canScrollNext,
            }}
        >
            <div
                onKeyDownCapture={handleKeyDown}
                className={cn('relative', className)}
                role="region"
                aria-roledescription="carousel"
                data-slot="carousel"
                {...props}
            >
                {children}
            </div>
        </CarouselContext.Provider>
    );
}

function CarouselContent({ className, ...props }: React.ComponentProps<'div'>) {
    const { carouselRef, orientation } = useCarousel();

    return (
        <div ref={carouselRef} className="overflow-hidden rounded-2xl" data-slot="carousel-content">
            <div
                className={cn('flex', orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col', className)}
                {...props}
            />
        </div>
    );
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
    const { orientation } = useCarousel();

    return (
        <div
            role="group"
            aria-roledescription="slide"
            data-slot="carousel-item"
            className={cn(
                'min-w-0 shrink-0 grow-0 basis-full',
                orientation === 'horizontal' ? 'pl-4' : 'pt-4',
                className,
            )}
            {...props}
        />
    );
}

function CarouselItemTitle({ className, ...props }: React.ComponentProps<'h2'>) {
    return <h2 className={cn('text-xl font-semibold', className)} {...props} />;
}

function CarouselItemSubtitle({ className, ...props }: React.ComponentProps<'p'>) {
    return <p className={cn('opacity-75', className)} {...props} />;
}
interface CarouselButton extends React.ComponentProps<typeof Button> {
    imageLength: number,
}
function CarouselPrevious({
    className,
    variant = 'outline',
    size = 'icon',
    color = 'primary',
    imageLength,
    onClick,
    ...props
}: CarouselButton) {
    const { orientation, scrollPrev, canScrollPrev, scrollTo } = useCarousel();
    const { formatMessage } = useIntl();

    const handlePreviousOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) onClick(event);
        if (canScrollPrev) scrollPrev()
        else scrollTo(imageLength - 1);
    }

    return (
        <Button
            data-slot="carousel-previous"
            variant={variant}
            size={size}
            className={cn(
                'absolute size-8 rounded-full',
                orientation === 'horizontal'
                    ? 'top-1/2 -left-12 -translate-y-1/2'
                    : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
                className,
            )}
            onClick={handlePreviousOnClick}
            {...props}
        >
            <div className="flex flex-row items-center">
                <FontAwesomeIcon icon={faChevronLeft} className="size-4" color={color} />
                <span className={cn(size === 'icon' && "sr-only")}>{formatMessage(generalMessages.previous)}</span>
            </div>
        </Button>
    );
}

function CarouselNext({
    className,
    variant = 'outline',
    size = 'icon',
    color = 'primary',
    onClick,
    ...props
}: React.ComponentProps<typeof Button>) {
    const { orientation, scrollNext, canScrollNext, scrollTo } = useCarousel();
    const { formatMessage } = useIntl();

    const handleNextOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) onClick(event);
        if (canScrollNext) scrollNext()
        else scrollTo(0);
    }
    return (
        <Button
            data-slot="carousel-next"
            variant={variant}
            size={size}
            className={cn(
                'absolute size-8 rounded-full',
                orientation === 'horizontal'
                    ? 'top-1/2 -right-12 -translate-y-1/2'
                    : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
                className,
            )}
            onClick={handleNextOnClick}
            {...props}
        >
            <div className="flex flex-row items-center">
                <span className={cn(size === 'icon' && "sr-only")}>{formatMessage(generalMessages.next)}</span>
                <FontAwesomeIcon icon={faChevronRight} className="size-4" color={color}/>
            </div>
        </Button>
    );
}

function CarouselDotButton({
    className,
    index,
    ...props
}: React.ComponentProps<'button'> & {
    index: number;
}) {
    const { api } = useCarousel();

    const onDotButtonClick = (index: number) => {
        api?.scrollTo(index);
    };

    return (
        <button
            type="button"
            onClick={() => onDotButtonClick(index)}
            className={cn(
                'size-2.5 rounded-full bg-white/50 hover:bg-white/70',
                api?.selectedScrollSnap() == index && 'bg-white hover:bg-white',
                className,
            )}
            {...props}
        />
    );
}

export {
    type CarouselApi,
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselItemTitle,
    CarouselItemSubtitle,
    CarouselPrevious,
    CarouselNext,
    CarouselDotButton,
};
